import { JsonObject, JsonProperty } from 'json2typescript';
import { ModelEntity, UtilsMisc, UtilsString } from '@pit/core';
import { IDtoUser, UserState } from '../interfaces/dto.user.interface';
import { IDtoMandator } from '../interfaces/dto.mandator.interface';
import { DtoMandator } from './dto.mandator';
import { IDtoUserRole } from '../interfaces/dto.user.role.interface';
import { DtoUserRole } from './dto.user.role';
import { IDtoDepot } from '../interfaces/dto.depot.interface';
import { DtoDepot } from './dto.depot';

// @dynamic
@JsonObject('DtoUser')
export class DtoUser extends ModelEntity implements IDtoUser {

    constructor(initValues: Partial<IDtoUser> = null) {
        super();

        if (initValues !== null) {
            Object.assign(this, initValues);
        }
    }

    @JsonProperty('firstName', String, true)
    public firstName?: string = null;

    @JsonProperty('lastName', String, true)
    public lastName?: string = null;

    @JsonProperty('number', String, true)
    public number?: string = null;

    @JsonProperty('password', String, true)
    public password?: string = null;

    @JsonProperty('passwordResetRequested', Boolean, true)
    public passwordResetRequested?: boolean = false;

    @JsonProperty('token', String, true)
    public token?: string = null;

    @JsonProperty('userName', String, true)
    public userName?: string = null;

    @JsonProperty('userNumber', String, true)
    public userNumber?: string = null;

    @JsonProperty('userState', Number, true)
    public userState?: UserState = UserState.Created;

    @JsonProperty('mandator', DtoMandator, true)
    public mandator?: IDtoMandator = null;

    @JsonProperty('depots', [DtoDepot], true)
    public depots?: IDtoDepot[] = [];

    @JsonProperty('userRoles', [DtoUserRole], true)
    public userRoles?: IDtoUserRole[] = [];

    public get completeDescription(): string {
        return UtilsString.stringFormat('{0} ({1})', this.fullName, this.userName).trim();
    }

    public get completeDescriptionExt(): string {
        return UtilsString.stringFormat('{0} ({1})', this.fullNameExt, this.userName).trim();
    }

    public get fullName(): string {
        return UtilsString.stringFormat('{0} {1}', this.firstName, this.lastName).trim();
    }

    public get fullNameExt(): string {
        return UtilsString.stringFormat('{0}, {1}', this.lastName, this.firstName).trim();
    }

    public get isAdmin(): boolean {
        return (UtilsMisc.isNullOrUndefined(this.userRoles.find(x => ((x.code === 'ADMIN') || (x.code === 'ADMINSUPER')))) === false);
    }

    public get isAdminSuper(): boolean {
        return (UtilsMisc.isNullOrUndefined(this.userRoles.find(x => (x.code === 'ADMINSUPER'))) === false);
    }

    public get isDispatcher(): boolean {
        return (UtilsMisc.isNullOrUndefined(this.userRoles.find(x => (x.code === 'DISPATCHER'))) === false);
    }

    public get isMobileUser(): boolean {
        return (UtilsMisc.isNullOrUndefined(this.userRoles.find(x => (x.code === 'MOBILE'))) === false);
    }

    public get isWebUser(): boolean {
        return ((this.isDispatcher === true) || (this.isAdmin === true));
    }
    // Guarded: without roles this read the element at index -1 and threw on the
    // undefined it got back.
    public get roleDescription(): string {
        if ((UtilsMisc.isNullOrUndefined(this.userRoles) === true) || (this.userRoles.length === 0)) {
            return '';
        }

        return this.userRoles[this.userRoles.length - 1].description;
    }

    protected override doSetReferences(): void {
        super.doSetReferences();

        if (UtilsMisc.isNullOrUndefined(this.userRoles) === false) {
            this.userRoles.sort((a, b) => (a.level - b.level));
        }
    }

}
